import React from "react";
import { Box, Typography } from "@mui/material";
import { getAppBrand } from "@/config/appBrands";

/**
 * Compact app-themed stat tile — app icon, label, value, all in the app's colour.
 *
 * Deliberately small so a whole row of them fits above a grid without stealing
 * the vertical space the grid needs. Use `AdminStatCard` when a screen has two or
 * three headline numbers that deserve the room; use this when there are several
 * and the point is comparing them at a glance.
 *
 * @param {string}   [app]     Key into APP_BRANDS ('quda' | 'medidrop' | …). Drives icon + colour.
 * @param {string}   [color]   Overrides the brand colour — for non-app tiles (e.g. At-Risk red).
 * @param {Function} [Icon]    MUI icon, used when the tile isn't app-branded.
 * @param {string}   label     Short caption, e.g. "Trials"
 * @param {*}        value     The number/text to display
 * @param {string}   [caption] Optional sub-line under the value
 * @param {Function} [onClick] Makes the tile actionable
 * @param {boolean}  [active]  Renders the tile as the currently selected one
 */
const AppStatChip = ({
  app,
  color,
  Icon,
  label,
  value,
  caption,
  onClick,
  active = false,
}) => {
  const brand = getAppBrand(app);
  const accent = color || brand.main;
  const clickable = typeof onClick === "function";

  return (
    <Box
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.5,
        py: 1,
        minWidth: 0,
        flex: "1 1 0",
        borderRadius: "8px",
        backgroundColor: active ? accent : "#fff",
        border: `1.5px solid ${accent}`,
        boxShadow: active ? `0 2px 6px -1px ${accent}66` : "none",
        cursor: clickable ? "pointer" : "default",
        transition: "background-color .15s, box-shadow .15s",
        "&:hover": clickable && !active ? { backgroundColor: brand.tint } : undefined,
        "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
      }}
    >
      {app && brand.icon ? (
        <Box
          component="img"
          src={brand.icon}
          alt=""
          sx={{
            width: 26,
            height: 26,
            flexShrink: 0,
            objectFit: "contain",
            // The RGB icons are dark-on-transparent; knock them to white when the
            // tile itself is filled with the brand colour.
            filter: active ? "brightness(0) invert(1)" : "none",
          }}
        />
      ) : Icon ? (
        <Icon sx={{ fontSize: 24, color: active ? "#fff" : accent, flexShrink: 0 }} />
      ) : null}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            color: active ? "rgba(255,255,255,0.85)" : "#6b7280",
            lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {caption ? (
          <Typography
            noWrap
            sx={{ fontSize: "0.68rem", color: active ? "rgba(255,255,255,0.8)" : "#9ca3af", lineHeight: 1.3 }}
          >
            {caption}
          </Typography>
        ) : null}
      </Box>

      <Typography
        sx={{
          fontSize: "1.5rem",
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: "-0.02em",
          color: active ? "#fff" : accent,
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default AppStatChip;
